import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Form, Icon } from 'semantic-ui-react'

import { getImageURL } from 'images'
import { ImageCrop } from 'ui'

import { actions, selectors } from '../redux'

import type { FC } from 'react'

import type { TeamSchema } from 'teams'

export interface EditProps {
  team: TeamSchema
}

const rgbToHex = (rgb = '') =>
  rgb
    ? `#${rgb
        .split(',')
        .map((num) => Number(num).toString(16).padStart(2, '0'))
        .join('')
        .substring(0, 6)}`
    : '#FFFFFF'

const hexToRgb = (hex = '') => {
  let alpha = hex.replaceAll(/[^0-9a-f]+/gi, '').substring(0, 6)
  if (alpha.length < 6) {
    alpha = alpha
      .split('')
      .map((char) => char + char)
      .join('')
      .substring(0, 6)
  }
  return alpha.length === 6
    ? alpha
        .match(/(.{2})/g)
        ?.map((char) => parseInt(char, 16))
        .join(',') || null
    : null
}

const Edit: FC<EditProps> = ({ team }) => {
  const dispatch = useDispatch()
  const [avatarURL, setAvatarURL] = useState(
    team.avatar?.filename ? getImageURL(team.avatar.filename, '200x200-fit') : '',
  )
  const [avatar, setAvatar] = useState<Blob | undefined>()
  const { loading } = useSelector(selectors.team)

  const { formState, handleSubmit, register } = useForm({
    defaultValues: {
      color_primary: rgbToHex(team.color_primary),
      color_secondary: rgbToHex(team.color_secondary),
      name: team.name,
      nickname: team.nickname,
    },
  })

  const onRemoveAvatar = () => {
    setAvatarURL('')
    setAvatar(undefined)
  }

  const onCutAvatar = (url: string, blob: Blob) => {
    setAvatarURL(url)
    setAvatar(blob)
  }

  const onSubmit = (values: { color_primary: string; color_secondary: string; name: string; nickname: string }) => {
    dispatch(
      actions.teamUpdate({
        ...values,
        id: team.id,
        _avatar: !avatar && !avatarURL,
        avatar,
        color_primary: hexToRgb(values.color_primary),
        color_secondary: hexToRgb(values.color_secondary),
      }),
    )
  }
  return (
    <div className="bg-white p-2">
      <h3>Editar el equipo</h3>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-wrap gap-2 items-start">
          <div className="w-52">
            <ImageCrop
              contentClassName="drop-shadow border"
              cropHeight={200}
              cropWidth={200}
              onCut={onCutAvatar}
              onRemove={onRemoveAvatar}
              readOnly={loading}
              src={avatarURL}
            >
              <div>
                <Icon name="shield" size="big" />
                <br />
                Agrega el escudo de tu equipo
              </div>
            </ImageCrop>
          </div>
          <div className="min-w-72 max-w-96">
            <div className="flex gap-2">
              <Form.Input className="w-24" label="Color 1" readOnly>
                <input {...register('color_primary')} className="!p-0" type="color" />
              </Form.Input>
              <Form.Input className="w-24" label="Color 2" readOnly>
                <input {...register('color_secondary')} className="!p-0" type="color" />
              </Form.Input>
            </div>
            <Form.Input error={formState.errors.name?.message} label="Nombre" readOnly={loading} required>
              <input
                {...register('name', {
                  maxLength: {
                    message: 'Máximo 100 caracteres',
                    value: 100,
                  },
                  required: 'El nombre es requerido',
                })}
                maxLength={100}
                type="text"
              />
            </Form.Input>
            <Form.Input
              className="!mb-0"
              error={formState.errors.nickname?.message}
              label="Nombre Corto"
              readOnly={loading}
              required
            >
              <input
                {...register('nickname', {
                  maxLength: {
                    message: 'Máximo 10 caracteres',
                    value: 10,
                  },
                  required: 'El nombre corto es requerido',
                })}
                maxLength={10}
                type="text"
              />
            </Form.Input>
            <div className="text-sm py-1">
              <Icon className="text-orange" name="warning sign" /> Te recomendamos <b>no editar</b> el nombre corto pues
              puede generar conflictos con los procesos automatizados del sistema.
            </div>
          </div>
        </div>
        <div>
          <Button
            content={loading ? 'Guardando...' : 'Guardar'}
            icon={loading ? 'spinner' : 'save'}
            primary
            readOnly={loading}
            type="submit"
          />
        </div>
      </Form>
    </div>
  )
}

export default Edit
